import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Row, Card, Col, Collapse, Table, Form } from "react-bootstrap";
import { abpnClient } from "../../../Services/AbpnClient";
import { CreditCardClientLibrary, PaymentProcesserEnum, getCreditCardProcessor, getPaymentProcessorForPaymentType } from "../../../Services/PaymentProcessor/PaymentProcessor";
import Payment from "../../../Services/PaymentProcessor/Payment";
import { AuthContext } from "../../Auth/AuthProvider";
import Physician from "../../Physician/Models/Physician";
import { deepCopy } from "../../Shared/Utility/DeepCopy";
import ApplicationModel, { CandidateStatus } from "../Models/ApplicationModel";
import Examination from "../Models/Examination";
import { FeeType } from "../Models/Fee";
import { Alert } from 'react-bootstrap';
import { Fragment } from "react";
import { AbpnSpinner } from './../../Shared/Components/AbpnSpinner';
import { PaymentContainer } from "../../../Services/PaymentProcessor/PaymentContainer";
import ReceiptModel from "../../Shared/Models/ReceiptModel";
import InvoiceModel from "../../Shared/Models/InvoiceModel";
import InvoiceLineItemModel from "../../Shared/Models/InvoiceLineItemModel";
import { configService } from "../../../Services/Config/ConfigService";
import { booleanify } from "../../Shared/Utility/Booleanify";
import AffirmCheckout from "../../../Services/PaymentProcessor/AffirmCheckout";
import { PaymentType } from "../../../Services/PaymentProcessor/PaymentType";
import { InvoiceStatus } from "../../Shared/Enums/InvoiceStatus";
import { usePrevious } from "../../Shared/Utility/usePrevRefHook";
import { useHistory } from "react-router-dom";
import isNullOrUndefined from "../../Shared/Utility/isNullOrUndefined";

interface IInvoicePaymentCardProps {
    application: ApplicationModel;
    physician: Physician;
    examination: Examination;
    isSectionAvailable: boolean;
    isSectionOpen: boolean;
    onApplicationSubmitted: (receipt: ReceiptModel) => void;
    examinationDates: string;
    onOpen: () => void;
    disabled: boolean;
    applicationDeadlineDate: string;
    totalBalance: number;
}


interface IPaymentPlan {
    isPayInFull: boolean;
    isPayOverTime: boolean;
}

export function InvoicePaymentCard(props: IInvoicePaymentCardProps) {
    const hasPaymentPlan = false;

    const [openCollapse, setOpenCollapse] = useState<boolean>(props.isSectionOpen);
    const [invoices, setInvoices] = useState<Array<InvoiceModel>>([]);
    const unpaidInvoices = invoices.filter(_ => _.isPaid == false);
    const unpaidLineItems = unpaidInvoices?.flatMap(_ => _.lineItems);
    const [isLoadingInvoices, setIsLoadingInvoices] = useState<boolean>(false);
    const [canSelectPaymentPlan, setCanSelectPaymentPlan] = useState<boolean>(false);
    const [creditCardProcessor, setCreditCardProcessor] = useState<CreditCardClientLibrary>(CreditCardClientLibrary.None);
    const [previouslyPaidInvoices, setPreviouslyPaidInvoices] = useState<Array<InvoiceModel>>([]);
    const history = useHistory();
    
    const contactAddress = props.physician.addresses.addresses.find(_ => _.isContact);

    const [payment, setPayment] = useState<Payment>({ email: props.physician.emailAddress, city: contactAddress?.city, state: contactAddress?.stateCode, hasBeenCharged: false, routingNumber: "" } as Payment);
    let isPaymentChargedSubmitted = usePrevious(false); //track whether to submit to invoice if payment has been charged

    const [paymentPlan, setPaymentPlan] = useState<IPaymentPlan>({
        isPayInFull: false,
        isPayOverTime: false,
    })

    const setPayInFull = () => {
        var updated = deepCopy(paymentPlan);
        updated.isPayInFull = true;
        updated.isPayOverTime = false;
        setPaymentPlan(updated);

        var updatedPayment = deepCopy(payment);
        updatedPayment.IsValid = null;
        setPayment(updatedPayment);
    }

    const setPayOverTime = () => {
        var updated = deepCopy(paymentPlan);
        updated.isPayInFull = false;
        updated.isPayOverTime = true;
        setPaymentPlan(updated);

        var updatedPayment = deepCopy(payment);
        updatedPayment.IsValid = null;
        setPayment(updatedPayment);
    }

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isPaymentSuccessful, setIsPaymentSuccessful] = useState<boolean>(null);
    const [isDisable, setIsDisable] = useState<boolean>(false);

    const payContainerRef = React.useRef(null);

    const hasInvoice = (list : Array<InvoiceModel>) => list.length > 0;

    const formRef = useRef(null);

    useEffect(() => {
        const loadInvoices = async () => {
            setIsLoadingInvoices(true);

            //Get invoices related to the provided application.  I would really rather this component was passed the invoices as props.
            const results = (await abpnClient.get<Array<InvoiceModel>>('/api/invoice/getInvoicesForPhysician', authContext))
                .filter(_ => _.lineItems?.filter(x => x.product.examinationId == props.application.examinationId).length > 0)
                .filter(x => !(x.invoiceStatus == InvoiceStatus.Abandoned ||
                               x.invoiceStatus == InvoiceStatus.WrittenOff));

            // if there are no results with an exam fee, we need to create an invoice.
            let isMissingInvoiceWithExamFee = results?.some(invoice => 
                invoice.lineItems?.some(lineItem => 
                  isExamFee(lineItem.product.feeTypeId) &&
                  lineItem.product.examinationId === props.application.examinationId &&
                  !lineItem.isRefunded
                )
              ) === false;

            if(isMissingInvoiceWithExamFee) {
                let url = `/api/invoice/createInvoiceForApplication?applicationId=${props.application.applicationId}&persist=${false}`;
                const newInvoice = await abpnClient.post<InvoiceModel>(url, null, authContext);
                results.push(newInvoice);
            }

            let newInvoices = new Array<InvoiceModel>();
            results.filter(x => x.isPaid == false && !(x.invoiceStatus == InvoiceStatus.Abandoned || x.invoiceStatus == InvoiceStatus.WrittenOff || x.invoiceStatus == InvoiceStatus.Paid || x.invoiceStatus == InvoiceStatus.PaidReceivable)).forEach(_ => {
                newInvoices.push(new InvoiceModel({invoiceNumber: _.invoiceNumber, createDate: _.createDate, isPaid: _.isPaid, lineItems: _.lineItems}));
            });
            
            
            let prevPaidInvoices = new Array<InvoiceModel>();
            results.filter(x=>x.invoiceStatus == InvoiceStatus.Paid).forEach(_ => {
                prevPaidInvoices.push(new InvoiceModel({invoiceNumber: _.invoiceNumber, createDate: _.createDate, isPaid: _.isPaid, lineItems: _.lineItems}));
                setPreviouslyPaidInvoices(prevPaidInvoices);
            });

            setInvoices(newInvoices);
            setIsLoadingInvoices(false);
        }

        if (!isLoadingInvoices && props.isSectionOpen) {
            loadInvoices();
        }
    }, [props.isSectionOpen])

    useEffect(() => {
        if (creditCardProcessor !== CreditCardClientLibrary.None) return;
        
        const getCreditCardProcessorForPaymentContainer = async () => {
            const cardProcessor : CreditCardClientLibrary = await getCreditCardProcessor();
            setCreditCardProcessor(cardProcessor);
        }

        getCreditCardProcessorForPaymentContainer();

    }, [])

    useEffect(() => {
        const getCanSelectPaymentPlan = async () => {
            let k = (await configService.getConfiguration()).FeatureFlags.AllowPayOverTime;
            if (typeof k === "string")
            {
                setCanSelectPaymentPlan(booleanify(k));
            }
            else {
                setCanSelectPaymentPlan(k);
            }

        }
        getCanSelectPaymentPlan();
    }, []);

    const getInitialBalanceDue = () => invoices?.filter(_ => _.isPaid == false).reduce((sum, _) => sum + _.amountDue(), 0);

    const balanceDue = getInitialBalanceDue();
    
    const hasBalanceDue = balanceDue > 0;

    const authContext = useContext(AuthContext);

    const onPaymentChanged = (updated: Payment) => {
        setPayment(deepCopy(updated));
    }

    const replaceUnsavedInvoice = async () => {
        let newArrayOfInvoices = new Array<InvoiceModel>();
        
        invoices.filter(_ => _.invoiceNumber?.length > 0).forEach(_ => {
            newArrayOfInvoices.push(_);
        });

        let isMissingInvoiceWithExamFee = newArrayOfInvoices?.filter(_ => 
            _.lineItems?.filter(x => isExamFee(x.product.feeTypeId) && x.product.examinationId == props.application.examinationId).length > 0)
            .length == 0;
        
        if (isMissingInvoiceWithExamFee) {
            let url = `/api/invoice/createInvoiceForApplication?applicationId=${props.application.applicationId}&persist=${true}`;
            const newInvoice = await abpnClient.post<InvoiceModel>(url, null, authContext);
            newArrayOfInvoices.push(new InvoiceModel(newInvoice));
        }

        setInvoices(newArrayOfInvoices);
        return newArrayOfInvoices;
    }

    const onAffirmCheckoutComplete = () => {
        formRef.current.dispatchEvent(
            new Event("submit", { cancelable: true, bubbles: true })
        );

    }

    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        setIsDisable(true);

        if (isSubmitting)
            return;

        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.classList.add('was-validated');

        await submitPayment();
    }

    //Automatically submit the payment form is the payment was already charged through the payment processor client library
    useEffect(() => {
        async function checkShouldSubmitAfterPaymentCharged(){
            if (isPaymentChargedSubmitted.current == false && payment.hasBeenCharged == true){
                isPaymentChargedSubmitted.current = true;
                try{
                    await submitPayment();
                }
                finally{
                    resetPaymentCharge();
                }
            }
        }

        checkShouldSubmitAfterPaymentCharged();
    }, [payment.hasBeenCharged, isPaymentChargedSubmitted]);

    const resetPaymentCharge = () => {
        isPaymentChargedSubmitted.current = false;
        setPayment({...payment, hasBeenCharged: false, paymentCharge: null});
    }

    const refreshPaymentAmount = () => {
        onPaymentChanged({...payment, amount: getInitialBalanceDue()});
    }

    useEffect(() => {
        refreshPaymentAmount();
    }, [invoices]);

    /**Handle payment processing errors with redirect to error page with custom error message.
     * This custom error should only occur on payment errors, not application submission errors */
    const handlePaymentError = () => {
        history.replace("/error/PaymentProcessingError");
    }
    
    const submitPayment = async () => {
        if (isSubmitting) {
            return;
        }

        setIsDisable(true);


        var noPaymentIsDue = isNullOrUndefined(payContainerRef) || !hasBalanceDue
        if (noPaymentIsDue)
        {
            setIsSubmitting(true);

            if(previouslyPaidInvoices.length > 0)
            {
                var invoiceReceipt : any;
                await abpnClient.post(`/api/application/submitApplicationByExaminationId?examinationId=${props.application.examinationId}`,null, authContext);
                props.onApplicationSubmitted(invoiceReceipt);
                return;
            }

            let updateInvoices = await replaceUnsavedInvoice();
            let invoiceNumbers = updateInvoices.map(_ => _.invoiceNumber);
            var invoiceReceipt : any = await abpnClient.post<ReceiptModel>('/api/invoice/completeInvoiceWithNoPayment',invoiceNumbers, authContext);

            await abpnClient.post(`/api/application/submitApplicationByExaminationId?examinationId=${props.application.examinationId}`,null, authContext);

            //Map untyped objects to typed ones.
            let newResults = new Array<InvoiceModel>();
            invoiceReceipt.invoices.forEach((_ : any) => {
                newResults.push(new InvoiceModel({invoiceNumber: _.invoiceNumber, createDate: _.createDate, isPaid: _.isPaid, lineItems: _.lineItems}));
            });

            invoiceReceipt.invoices = newResults;

            props.onApplicationSubmitted(invoiceReceipt);
            return;
        }

        // Card or Affirm
        var updatedPayment = deepCopy(payment);
        const paymentProcessor = getPaymentProcessorForPaymentType(creditCardProcessor, updatedPayment.paymentType);
        if (paymentProcessor.getPaymentProcessorId() !== PaymentProcesserEnum.CardX && (payment.paymentType === PaymentType.CreditCardVisa || payment.paymentType === PaymentType.ECheckAch)){
            
            // If it's card or echeck but not cardX processor, tokenize the payment details, if it's affirm, the token already is populated
            if (payContainerRef.current.validate(payment) && formRef.current.checkValidity()) {
                formRef.current.classList.remove('was-validated');

                
                updatedPayment = await paymentProcessor.tokenize(updatedPayment);
            }else{
                return;
            }
        }

        updatedPayment.amount = invoices.reduce((sum, _) => sum + _.amountDue(), 0);
        setPayment(updatedPayment);

        if (!updatedPayment?.IsValid) {
            setIsDisable(false);
            return;
        }

        setIsDisable(false);
        setIsSubmitting(true);

        let updatedInvoices = await replaceUnsavedInvoice();

        if (hasInvoice(updatedInvoices)) {

            // This is where the invoice should be created and app submitted.
            let invoiceNumbers = updatedInvoices.map(_ => _.invoiceNumber);
            const invoicePaymentRequest = {
                invoiceNumbers: invoiceNumbers,
                paymentCharge: payment.paymentCharge
            };

            let invoiceReceipt : any = await abpnClient.post<ReceiptModel>(`/api/invoice/applyPaymentToInvoices?paymentType=${updatedPayment.paymentType}&paymentProcessor=${paymentProcessor.getPaymentProcessorId()}&paymentToken=${updatedPayment.PaymentToken}&paymentAmount=${updatedPayment.amount}`, invoicePaymentRequest, authContext, {handleErrorRedirect: false})
                .catch(handlePaymentError);

            //If previous API call failed and was handled, prevent application submission
            if (isNullOrUndefined(invoiceReceipt) || !invoiceReceipt.isSuccessful) {
                setIsSubmitting(false);
                setIsPaymentSuccessful(false);
                return;
            }

            // Don't submit the app if it has already been submitted
            if (props.application.candidateStatus == CandidateStatus.Unsubmitted) {
                await abpnClient.post(`/api/application/submitApplicationByExaminationId?examinationId=${props.application.examinationId}`,null, authContext, {handleErrorRedirect: true});
            }                    

            let reloadedInvoices = new Array<InvoiceModel>();
            invoiceReceipt.invoices.forEach((_:any) => {
                reloadedInvoices.push(new InvoiceModel({invoiceNumber: _.invoiceNumber, createDate: _.createDate, isPaid: _.isPaid, lineItems: _.lineItems}));
            });
            invoiceReceipt.invoices = reloadedInvoices;

            setIsSubmitting(false);
            setIsPaymentSuccessful(invoiceReceipt.isSuccessful);

            if (!invoiceReceipt.isSuccessful) {
                return;
            }

            props.onApplicationSubmitted(invoiceReceipt);
            return;
        }
    
        setIsDisable(false);
    }

    const isExamFee = (feeTypeId : number) => {
        return (feeTypeId == FeeType.examFeeMoc ||
                feeTypeId == FeeType.examFeeMocPrimaryInitial ||
                feeTypeId == FeeType.examFeeMocWithThreeOrMoreModules ||
                feeTypeId == FeeType.examFeeMocWithTwoModules ||
                feeTypeId == FeeType.examFeeSubspecialtyinitial)
    }

     const displayInvoiceLineItem = (item : InvoiceLineItemModel) => {
        return (<Fragment>
                    <tr key={item.product.feeTypeId}>
                        <td >{item.description}</td>
                        <td className="text-right">${item.unitPrice}</td>
                    </tr>
                    {displayInvoiceLineItemDiscount(item)}
                </Fragment>)
    }

    const displayInvoiceLineItemDiscount = (item : InvoiceLineItemModel) => {
        if (item.discountAmount > 0) return (
            <tr key={item.product.feeTypeId}>
                <td>{item.discountDescription}</td>
                <td className="text-right">$-{item.discountAmount}</td>
            </tr>
        );
        else return;
    } 

    useEffect(() => {
        setOpenCollapse(props.isSectionOpen);
    }, [props.isSectionOpen]);

    useEffect(() => {
        if (openCollapse) props.onOpen();
    }, [openCollapse]);
    
    //Can submit form if it's not already submitting or has an unpaid invoice with a balance due
    const canSubmitForm = !isDisable && !isLoadingInvoices && !(hasInvoice && hasBalanceDue);

    return (
        <Card className="mb-4" border="pp-secondary-grey">
            <Card.Body>
                <Card.Title className="mb-0 font-weight-bolder" onClick={() => { if (!props.disabled) setOpenCollapse(!openCollapse) }}>
                    <span className={`text-pp-primary ${props.disabled && 'pp-disabled'}`}><i className="font-weight-bolder las la-angle-down"></i> Payment</span>
                </Card.Title>
                <Collapse in={openCollapse}>
                    <Form noValidate onSubmit={(e) => submit(e)} ref={formRef}>
                        {isSubmitting ? <AbpnSpinner /> :
                            <Row className="mt-3">
                                <Col xs={12} md={4} lg={4}>
                                    <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller">
                                        Applicant Name
                                    </Card.Text>
                                    <Card.Text className="pp-font-size-smaller">
                                        {props.physician.fullName}
                                    </Card.Text>
                                    <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller">
                                        Email
                                    </Card.Text>
                                    <Card.Text className="pp-font-size-smaller">
                                        {props.physician.emailAddress}
                                    </Card.Text>
                                    <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller">
                                        Examination Requested
                                    </Card.Text>
                                    <Card.Text className="pp-font-size-smaller">
                                        {props.examination.title}
                                    </Card.Text>
                                    <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller">
                                        Examination Dates
                                    </Card.Text>
                                    <Card.Text className="pp-font-size-smaller">
                                        {props.examinationDates}
                                    </Card.Text>

                                    {isLoadingInvoices ? <AbpnSpinner justifyContent="start"/> : (invoices?.filter(_ => _.isPaid == false).length > 0) &&
                                    <Fragment>
                                        <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller">
                                            <div>Fees</div>
                                        </Card.Text>
                                        <Table variant="sm" className="pp-fee-table pp-font-size-smaller">
                                            <tbody>
                                                {unpaidLineItems.map(_ => { return (displayInvoiceLineItem(_))})}
                                                <tr>
                                                    <th className="font-weight-bolder">Total</th>
                                                    <td className="text-right">${balanceDue}</td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </Fragment>}

                                    {hasBalanceDue &&
                                        <Fragment>
                                            <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller" hidden={props.examination.isMoc}>
                                                Select Payment Option<span className="text-pp-primary ml-2">*</span>
                                            </Card.Text>
                                            <Button
                                                variant={paymentPlan.isPayInFull ? "pp-primary" : "outline-pp-primary"}
                                                className="mt-1 mr-2"
                                                onClick={() => setPayInFull()}>Pay In Full</Button>
                                             {canSelectPaymentPlan && hasBalanceDue && (!props.examination.isMoc) &&<Button
                                                variant={paymentPlan.isPayOverTime ? "pp-primary" : "outline-pp-primary"}
                                                className="mt-1"
                                                onClick={() => setPayOverTime()}
                                                hidden={props.examination.isMoc}>Pay Over Time With Affirm</Button>}
                                        </Fragment>
                                    }

                                    {(!canSelectPaymentPlan || hasPaymentPlan || (canSelectPaymentPlan && paymentPlan.isPayInFull)) && hasBalanceDue &&
                                        <Fragment>
                                            <PaymentContainer ref={payContainerRef} payment={payment} onPaymentChanged={onPaymentChanged} onPaymentTypeChanged={() => null} />
                                            {payment.paymentType && payment.IsValid === false &&

                                                <Alert className="pp-font-size-smaller" variant="pp-alert-danger">
                                                    <p><b>Could Not Validate Payment Method.</b> Please verify your payment method and try again</p>
                                                </Alert>}
                                            {isPaymentSuccessful === false &&
                                                <Alert className="pp-font-size-smaller" variant="pp-alert-danger">
                                                    <p><b>The provided payment method failed.</b> Please verify your payment method and try again</p>
                                                </Alert>}

                                        </Fragment>
                                    }

                                    {((canSelectPaymentPlan && paymentPlan.isPayOverTime)) && hasBalanceDue &&
                                        <Fragment>
                                            <AffirmCheckout physician={props.physician} invoices={invoices} payment={payment} onPaymentChanged={onPaymentChanged} onPaymentTypeChanged={() => null} onAffirmCheckoutComplete={onAffirmCheckoutComplete} />
                                        </Fragment>
                                    }

                                    {!isSubmitting &&
                                        <div>
                                            <Button type="submit"
                                                className="mt-4"
                                                variant="pp-primary"
                                                disabled={!canSubmitForm}>{`${"Submit Application"}`}
                                            </Button>
                                        </div>
                                    }
                                </Col>
                            </Row>
                        }
                    </Form>
                </Collapse>
            </Card.Body>
        </Card >
    );
}

export default InvoicePaymentCard;
